import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, comics } from '@/mocks/comics';

export default function CategoryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const tagMap: Record<string, string> = { rexue: '热血', aiqing: '爱情', xuanyi: '悬疑', qihuan: '奇幻', gaoxiao: '搞笑' };
  const filteredComics = slug
    ? (slug === 'hanman' || slug === 'riman' || slug === 'guoman'
        ? comics.filter((c) => c.category === slug)
        : comics.filter((c) => c.tags.includes(tagMap[slug] || '')))
    : [];

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-lg font-semibold text-foreground-900 mb-2">分类未找到</h2>
        <Link to="/category" className="text-sm text-primary-600 hover:text-primary-700 no-underline">
          返回全部分类
        </Link>
      </div>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `妖精动漫${category.name} - ${category.description}`,
    description: `妖精动漫${category.name}专区，${category.description}。全集免费在线阅读，下拉式流畅翻页。`,
    url: `https://yaojingdongman.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredComics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'ComicSeries',
          name: comic.title,
          url: `https://yaojingdongman.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`妖精动漫${category.name} | ${category.description} | 妖精动漫免费阅读`}
        description={`妖精动漫${category.name}专区，${category.description}。韩国漫画、日本漫画、国产漫画免费全集在线阅读，下拉式流畅翻页。`}
        keywords={`妖精动漫${category.name},${category.name}漫画,韩国漫画,日本漫画,国产漫画`}
        canonicalUrl={`https://yaojingdongman.com/category/${slug}`}
        schema={categorySchema}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '全部分类', path: '/category' },
            { label: category.name },
          ]}
        />

        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
            <i className={`${category.icon} text-2xl`}></i>
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">{category.description} · 共{category.count}部</p>
          </div>
        </div>

        {filteredComics.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    width={300}
                    height={400}
                  />
                  {comic.status === '已完结' && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-accent-500/90 text-background-50 text-xs font-medium">
                      完结
                    </span>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-eye-line text-white/80 text-xs"></i>
                      <span className="text-white/80 text-xs">
                        {comic.views > 1000000
                          ? `${(comic.views / 10000).toFixed(0)}万`
                          : `${(comic.views / 10000).toFixed(1)}万`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                    {comic.title}
                  </h3>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-foreground-500">{comic.author}</span>
                    <span className="text-xs text-accent-600 font-medium flex items-center gap-0.5">
                      <i className="ri-star-fill text-[10px]"></i>
                      {comic.rating}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-book-open-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类下暂无漫画</p>
          </div>
        )}
      </div>
    </>
  );
}